import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'

import { AuthChrome, PasswordField } from '../../components/AuthChrome'
import { Button, Field, Notice } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'

/**
 * Self-registration. Net-new work, not a port — §3.3 is right that AIVY is
 * invite-only at the commit this sprint ports from.
 *
 * What a new account gets is deliberately stated up front rather than after the
 * fact: BrainSpot's pool allows self-registration, but Sprint 1.3's gate means
 * the account lands in `pending_approval` and can reach nothing until an
 * administrator approves it. Saying so here is the difference between a wait
 * someone expects and a product that appears broken.
 */
export function SignUp() {
  const { signUp, authError } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password) {
      setLocalError('Enter an email and a password.')
      return
    }
    setLocalError(null)
    setIsSubmitting(true)
    try {
      const result = await signUp(email, password)
      navigate(
        result === 'confirm_required'
          ? `/verify-email?email=${encodeURIComponent(email.trim().toLowerCase())}`
          : '/sign-in',
        { replace: true },
      )
    } catch {
      // authError carries it.
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthChrome
      eyebrow="New account"
      title="Create your baseline"
      lead="BrainSpot learns what normal looks like for you. It starts with an account."
      footer={
        <span>
          Already have one? <Link className="auth__link" to="/sign-in">Sign in</Link>
        </span>
      }
    >
      <form className="auth__form" onSubmit={handleSubmit} noValidate>
        {(localError || authError) && <Notice tone="error">{localError ?? authError}</Notice>}

        <Field
          label="Email"
          type="email"
          value={email}
          autoComplete="username"
          autoFocus
          hint="We will send a verification code here."
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          hint="At least 8 characters."
        />

        <Notice tone="info">
          New accounts are reviewed by an administrator before they can be used. You
          will verify your email first, then see where your request stands.
        </Notice>

        <Button type="submit" full busy={isSubmitting}>
          {isSubmitting ? 'Creating your account' : 'Create account'}
        </Button>
      </form>
    </AuthChrome>
  )
}
